import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { JumpingDots } from "../../../UIToolkit/Animations/JumpingDots";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import { EmptyState } from "../../../UIToolkit/molecules/EmptyState";
import { SheetStepSectionLabel } from "../../../UIToolkit/Popups/SheetStepSectionLabel";
import { distinctStaffEntries } from "../gapsDayModel";
import type { GapsScopedShiftGroup } from "../gapsGroupSheets";
import { GapsAddCandidateRow } from "./GapsAddCandidateRow";
import type { GapsAddCandidate } from "./useGapsAddCandidates";

type Props = {
	/** What the list is and how it is ordered — "Least overtime, then lowest cost · 492 could take it". */
	listLabel: string;
	isRanking: boolean;
	/** The rows shown now: the shortlist, everyone, or the search's matches. */
	visible: GapsAddCandidate[];
	/** The list is the backend's cost order, unsearched — its first row is the ranking's own answer. */
	isRankedList: boolean;
	selectedId: number | null;
	/** The day's groups, building-wide, to say who already works a shift that day. */
	dayGroups: readonly GapsScopedShiftGroup[];
	/** An add is running: no row takes a pick. */
	isBusy: boolean;
	/** Stable across renders, so a memoised row that did not change skips re-rendering. */
	onPick: (candidate: GapsAddCandidate, index: number) => void;
	/** How many of the matches the shortlist is hiding. */
	hiddenCount: number;
	onShowMore: () => void;
	poolSize: number;
	matchCount: number;
	/** The search as typed, trimmed; `""` for none. */
	query: string;
	testIdPrefix: string;
};

/**
 * The add sheet's list (P3-7): its label, the people as one radio group (picking selects; the footer's Add is the
 * write), "Show N more" behind the shortlist, and the empty states — nobody at all, or nobody matching the search.
 *
 * Each row says whether the person already works a shift that day, since nobody is left out for that (the warnings
 * catch a real overlap).
 */
export const GapsAddCandidateList: FC<Props> = ({
	listLabel,
	isRanking,
	visible,
	isRankedList,
	selectedId,
	dayGroups,
	isBusy,
	onPick,
	hiddenCount,
	onShowMore,
	poolSize,
	matchCount,
	query,
	testIdPrefix,
}) => {
	const { t } = useTranslation();
	// Who already works on the day, by the group that lists them first — the row's "on Aide 6a-6p that day".
	const shiftThatDayByNurse = useMemo(() => {
		const byNurse = new Map<number, string>();
		for (const dayGroup of dayGroups) {
			for (const entry of distinctStaffEntries([dayGroup])) {
				const nurseId = entry.slot.nurseInShift.nurseId;
				if (!byNurse.has(nurseId)) byNurse.set(nurseId, `${dayGroup.jobLabel} ${dayGroup.timeLabel}`);
			}
		}
		return byNurse;
	}, [dayGroups]);

	return (
		<>
			<SheetStepSectionLabel dataTestId={`${testIdPrefix}-list-label`}>{listLabel}</SheetStepSectionLabel>

			{isRanking ? (
				<Box data-testid={`${testIdPrefix}-ranking`} sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 2.5 }}>
					<JumpingDots />
					<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{t("gaps.addSheet.ranking")}</Typography>
				</Box>
			) : (
				<Box role="radiogroup" aria-label={t("gaps.addSheet.title")} data-testid={`${testIdPrefix}-list`}>
					{visible.map((candidate, index) => (
						<GapsAddCandidateRow
							key={candidate.nurse.id}
							candidate={candidate}
							index={index}
							isSelected={candidate.nurse.id === selectedId}
							// The backend's own first place (least overtime, then lowest cost — the list's label says so), named rather
							// than scored: it is the ranking's answer, not a new judgement. Only for the ranked, unsearched list.
							isRankedFirst={index === 0 && isRankedList}
							shiftThatDay={shiftThatDayByNurse.get(candidate.nurse.id)}
							disabled={isBusy}
							onPick={onPick}
							dataTestId={`${testIdPrefix}-candidate-${candidate.nurse.id}`}
						/>
					))}
				</Box>
			)}

			{!isRanking && hiddenCount > 0 && (
				<SheetActionRow
					icon={<ExpandMoreIcon />}
					label={t("gaps.addSheet.showMore", { count: hiddenCount })}
					onClick={onShowMore}
					dataTestId={`${testIdPrefix}-show-more`}
				/>
			)}
			{!isRanking && poolSize === 0 && <EmptyState dataTestId={`${testIdPrefix}-nobody`} title={t("gaps.addSheet.nobody")} sx={{ py: 4 }} />}
			{!isRanking && poolSize > 0 && matchCount === 0 && (
				<EmptyState dataTestId={`${testIdPrefix}-no-matches`} title={t("gaps.addSheet.noMatches", { query })} sx={{ py: 4 }} />
			)}
		</>
	);
};
